import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import DepositForm from './DepositForm';
import WithdrawalForm from './WithdrawalForm';
import OperationHistory from './OperationHistory';
import QualityVerification from './QualityVerification';

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const StatusBadge = styled.div`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.status) {
      case 'operational': return '#d4edda';
      case 'maintenance': return '#fff3cd';
      case 'offline': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'operational': return '#155724';
      case 'maintenance': return '#856404';
      case 'offline': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const TabContainer = styled.div`
  margin-bottom: 30px;
`;

const TabList = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  color: ${props => props.active ? '#667eea' : '#6c757d'};
  border-bottom: 3px solid ${props => props.active ? '#667eea' : 'transparent'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #667eea;
    background: #f8f9fa;
  }
`;

const TabContent = styled.div`
  min-height: 500px;
`;

const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div`
  font-size: 24px;
  margin-bottom: 10px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OperationCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const OperationHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OperationContent = styled.div`
  padding: 25px;
`;

const AlertBox = styled.div`
  background: ${props => {
    switch (props.type) {
      case 'warning': return '#fff3cd';
      case 'error': return '#f8d7da';
      case 'success': return '#d4edda';
      case 'info': return '#d1ecf1';
      default: return '#f8f9fa';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'warning': return '#856404';
      case 'error': return '#721c24';
      case 'success': return '#155724';
      case 'info': return '#0c5460';
      default: return '#495057';
    }
  }};
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'warning': return '#ffc107';
      case 'error': return '#dc3545';
      case 'success': return '#28a745';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  }};
  font-weight: 500;
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  margin-right: 10px;

  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }
  }

  &.secondary {
    background: #f8f9fa;
    color: #495057;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  }

  &.success {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }

  &.warning {
    background: #ffc107;
    color: #212529;
    
    &:hover {
      background: #e0a800;
    }
  }
`;

const CustodialOperations = () => {
  const [activeTab, setActiveTab] = useState('deposits');
  const [systemStatus, setSystemStatus] = useState('operational');
  const [pendingOperations, setPendingOperations] = useState([]);

  // Mock statistics
  const stats = {
    todayDeposits: 12,
    todayWithdrawals: 8,
    pendingVerifications: 3,
    totalValueProcessed: 2450000
  };

  const tabs = [
    { id: 'deposits', label: 'Deposits', icon: '📥' },
    { id: 'withdrawals', label: 'Withdrawals', icon: '📤' },
    { id: 'verification', label: 'Quality Verification', icon: '🔍' },
    { id: 'history', label: 'Operation History', icon: '📋' }
  ];

  const handleEmergencyStop = () => {
    setSystemStatus('maintenance');
    toast.error('Emergency stop activated. All operations suspended.');
  };

  const handleResumeOperations = () => {
    setSystemStatus('operational');
    toast.success('Operations resumed successfully.');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Container>
      {/* Header with Status */}
      <Header>
        <Title>Custodial Operations</Title>
        <StatusIndicator>
          <StatusBadge status={systemStatus}>
            {systemStatus === 'operational' && '🟢 Operational'}
            {systemStatus === 'maintenance' && '🟡 Maintenance'}
            {systemStatus === 'offline' && '🔴 Offline'}
          </StatusBadge>
          {systemStatus === 'operational' ? (
            <ActionButton className="warning" onClick={handleEmergencyStop}>
              🛑 Emergency Stop
            </ActionButton>
          ) : (
            <ActionButton className="success" onClick={handleResumeOperations}>
              ▶️ Resume Operations
            </ActionButton>
          )}
        </StatusIndicator>
      </Header>

      {/* System Status Alerts */}
      {systemStatus === 'maintenance' && (
        <AlertBox type="warning">
          ⚠️ System is currently under maintenance. All custodial operations are suspended.
        </AlertBox>
      )}

      {systemStatus === 'offline' && (
        <AlertBox type="error">
          🔴 System is offline. Please contact system administrator.
        </AlertBox>
      )}

      {stats.pendingVerifications > 0 && (
        <AlertBox type="info">
          📋 {stats.pendingVerifications} items pending quality verification.
        </AlertBox>
      )}

      {/* Quick Statistics */}
      <QuickStats>
        <StatCard>
          <StatIcon>📥</StatIcon>
          <StatValue>{stats.todayDeposits}</StatValue>
          <StatLabel>Deposits Today</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>📤</StatIcon>
          <StatValue>{stats.todayWithdrawals}</StatValue>
          <StatLabel>Withdrawals Today</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>🔍</StatIcon>
          <StatValue>{stats.pendingVerifications}</StatValue>
          <StatLabel>Pending Verification</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatIcon>💰</StatIcon>
          <StatValue>{formatCurrency(stats.totalValueProcessed)}</StatValue>
          <StatLabel>Total Value Processed</StatLabel>
        </StatCard>
      </QuickStats>

      {/* Navigation Tabs */}
      <TabContainer>
        <TabList>
          {tabs.map(tab => (
            <Tab
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon} {tab.label}
            </Tab>
          ))}
        </TabList>

        {/* Tab Content */}
        <TabContent>
          {activeTab === 'deposits' && (
            <OperationCard>
              <OperationHeader>
                <span style={{ fontSize: '24px' }}>📥</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>Commodity Deposits</h3>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    Process incoming physical commodities and mint corresponding tokens
                  </p>
                </div>
              </OperationHeader>
              <OperationContent>
                <DepositForm disabled={systemStatus !== 'operational'} />
              </OperationContent>
            </OperationCard>
          )}

          {activeTab === 'withdrawals' && (
            <OperationCard>
              <OperationHeader>
                <span style={{ fontSize: '24px' }}>📤</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>Commodity Withdrawals</h3>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    Process outgoing physical commodities and burn corresponding tokens
                  </p>
                </div>
              </OperationHeader>
              <OperationContent>
                <WithdrawalForm disabled={systemStatus !== 'operational'} />
              </OperationContent>
            </OperationCard>
          )}

          {activeTab === 'verification' && (
            <OperationCard>
              <OperationHeader>
                <span style={{ fontSize: '24px' }}>🔍</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>Quality Verification</h3>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    Verify quality and authenticity of deposited commodities
                  </p>
                </div>
              </OperationHeader>
              <OperationContent>
                <QualityVerification />
              </OperationContent>
            </OperationCard>
          )}

          {activeTab === 'history' && (
            <OperationCard>
              <OperationHeader>
                <span style={{ fontSize: '24px' }}>📋</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>Operation History</h3>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    Complete history of all custodial operations and events
                  </p>
                </div>
              </OperationHeader>
              <OperationContent>
                <OperationHistory />
              </OperationContent>
            </OperationCard>
          )}
        </TabContent>
      </TabContainer>
    </Container>
  );
};

export default CustodialOperations; 